import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography } from 'antd';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    ReferenceLine,
    Tooltip,
    Cell,
} from 'recharts';
import { IProject } from 'models/project';
import YAxisLabelTwoLines from './YAxisLabelTwoLines';

const { Text } = Typography;

interface ElectricLoadingProps {
    project: IProject;
}

// ── Style tokens (matching reference component) ───────────────────────────────
const TEXT_COLOR  = '#111827';
const GRID_COLOR  = '#E5E7EB';
const AXIS_STROKE = '#111827';

// ── Colors ────────────────────────────────────────────────────────────────────
const COLOR_POSITIVE = '#1890ff';
const COLOR_NEGATIVE = '#1890ff';
const COLOR_SELECTED = '#f97316';

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatValue = (v: number): string => {
    if (!Number.isFinite(v)) return String(v);
    return Number.isInteger(v) ? String(v) : v.toFixed(1);
};

// ── Component ─────────────────────────────────────────────────────────────────
const ElectricLoadingChart: React.FC<ElectricLoadingProps> = ({ project }) => {
    const [selectedIdx, setSelectedIdx] = useState<number>(-1);

    useEffect(() => {
        setSelectedIdx(-1);
    }, [project?.id]);

    // ── Data ──────────────────────────────────────────────────────────────────
    const data = useMemo(() => {
        const xs = project.results?.electric_loading_x ?? [];
        const ys = project.results?.electric_loading_y ?? [];
        const n = Math.min(xs.length, ys.length);
        return Array.from({ length: n }, (_, i) => ({
            slot:    xs[i],
            loading: ys[i],
        }));
    }, [project]);

    // ── Y-axis domain ─────────────────────────────────────────────────────────
    const { yMin, yMax, yTicks } = useMemo(() => {
        if (!data.length) return { yMin: -1.1, yMax: 1.1, yTicks: [-1, -0.5, 0, 0.5, 1] };

        const vals   = data.map(d => d.loading);
        const yMin   = Math.floor(Math.min(...vals) * 10) / 10 - 0.1;
        const yMax   = Math.ceil( Math.max(...vals) * 10) / 10 + 0.1;
        const step   = (yMax - yMin) / 4;
        const yTicks = Array.from({ length: 5 }, (_, i) =>
            Math.round((yMin + i * step) * 1000) / 1000,
        );
        return { yMin, yMax, yTicks };
    }, [data]);

    // ── Bar click ─────────────────────────────────────────────────────────────
    const handleBarClick = (_: any, idx: number) => {
        setSelectedIdx(prev => (prev === idx ? -1 : idx));
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Card style={{ width: '100%' }}>
            <Text strong style={{ fontSize: 16, color: TEXT_COLOR }}>Electric Loading</Text>

            <div style={{ width: '100%', height: 300, marginTop: 16 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={data}
                        margin={{ top: 10, right: 10, bottom: 20, left: 40 }}
                        barCategoryGap={0}
                        barGap={0}
                    >
                        {/* Grid: solid lines, horizontal only — matches reference */}
                        <CartesianGrid
                            horizontal={true}
                            vertical={false}
                            stroke="#D1D5DB"
                            strokeOpacity={0.8}
                        />

                        {/* X-axis */}
                        <XAxis
                            dataKey="slot"
                            axisLine={{ stroke: GRID_COLOR }}
                            tickLine={false}
                            tick={false}
                            label={{
                                value: 'Slots',
                                position: 'insideBottom',
                                dy: 14,
                                fill: AXIS_STROKE,
                                fontSize: 12,
                                textAnchor: 'middle',
                            }}
                        />

                        {/* Y-axis */}
                        <YAxis
                            width={60}
                            axisLine={{ stroke: GRID_COLOR }}
                            tickLine={false}
                            domain={[yMin, yMax]}
                            ticks={yTicks}
                            tickFormatter={formatValue}
                            tick={{ fontSize: 12, fill: AXIS_STROKE }}
                            label={{
                                content: (props: any) => (
                                    <YAxisLabelTwoLines
                                        {...props}
                                        lines={['Normalized', 'Electric Loading']}
                                        fill={AXIS_STROKE}
                                        fontSize={12}
                                        textAnchor="middle"
                                    />
                                ),
                            }}
                        />

                        <ReferenceLine y={0} stroke="#c0c6cc" strokeWidth={1} />

                        {selectedIdx >= 0 && data[selectedIdx] && (
                            <ReferenceLine
                                x={data[selectedIdx].slot}
                                stroke={COLOR_SELECTED}
                                strokeDasharray="4 2"
                                strokeWidth={1.5}
                            />
                        )}

                        {/* Tooltip */}
                        {/* <Tooltip
                            cursor={{ fill: 'rgba(24,144,255,0.06)' }}
                            content={({ active, payload }: any) => {
                                if (!active || !payload?.length) return null;
                                const { slot, loading } = payload[0].payload;
                                return (
                                    <div
                                        style={{
                                            background: '#ffffff',
                                            border: `1px solid ${GRID_COLOR}`,
                                            padding: 10,
                                            borderRadius: 10,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                                            fontSize: 12,
                                            color: TEXT_COLOR,
                                        }}
                                    >
                                        <div style={{ fontWeight: 700, marginBottom: 4 }}>
                                            Slot {slot}
                                        </div>
                                        <div style={{ color: COLOR_POSITIVE }}>
                                            Loading: {formatValue(loading)}
                                        </div>
                                    </div>
                                );
                            }}
                        /> */}

                        <Bar
                            dataKey="loading"
                            radius={[6, 6, 0, 0]}
                            isAnimationActive={false}
                            onClick={handleBarClick}
                        >
                            {data.map((entry, idx) => {
                                const base  = entry.loading >= 0 ? COLOR_POSITIVE : COLOR_NEGATIVE;
                                const color = idx === selectedIdx ? COLOR_SELECTED : base;
                                return (
                                    <Cell
                                        key={`cell-${idx}`}
                                        fill={color}
                                        stroke={color}
                                        opacity={selectedIdx >= 0 && idx !== selectedIdx ? 0.5 : 1}
                                        cursor="pointer"
                                    />
                                );
                            })}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </Card>
    );
};

export default ElectricLoadingChart;
